import React, { memo, useState, useEffect, useRef, useCallback } from "react";
import PropTypes from 'prop-types';
import {
    maxFileSizeForLibro,
    maxFileSizeForLibroMB,
    validFileTypesForLibro,
    saveFileString,
} from "../../../util/file-helpers";
import { ExternalDropZone, Upload, UploadFileStatus } from "@progress/kendo-react-upload";
import { Button } from "@progress/kendo-react-buttons";
import FluentUiIcon from '../../../assets/fluent-ui-icon';
import { GetDynamicFormFieldFile } from "../../../services/api/task-form-api";
import CustomFileList from './CustomFileList';
import './CustomFileDropZone.scss';
import "@progress/kendo-font-icons/dist/index.css";

const CustomFileDropZone = memo(({
    defaultFiles,
    onFilesChange,
    showDropZone = false,
    externalDropZoneHint = "",
    externalDropZoneNote = "",
    fromForm = false,
    isDisabled = false
}) => {
    const [firstRenderCompleted, setFirstRenderCompleted] = useState(false);
    const [initialFiles, setInitialFiles] = useState([]);
    const [files, setFiles] = useState([]);
    const [base64Files, setBase64Files] = useState([]);
    const [allFiles, setAllFiles] = useState([]);
    const [affectedFiles, setAffectedFiles] = useState([]);
    const [uploadResetKey, setUploadResetKey] = useState(0);
    const uploadRef = useRef(null);
    // Use ref to store the latest onFilesChange to avoid stale closure issues
    const onFilesChangeRef = useRef(onFilesChange);
    // Use ref to store the latest base64Files for computation in effects
    const base64FilesRef = useRef(base64Files);
    // Track initial files removed during a reset so they survive subsequent onFilesChange calls
    const removedInitialFilesRef = useRef([]);

    const onAdd = useCallback((event) => {
        if (isDisabled) {
            if (event && typeof event.preventDefault === 'function') {
                event.preventDefault();
            }
            return;
        }
        setFiles(event.newState);
    }, [isDisabled]);

    const onRemove = useCallback(async (event) => {
        if (isDisabled) {
            if (event && typeof event.preventDefault === 'function') {
                event.preventDefault();
            }
            return;
        }

        const affectedFiles = event.affectedFiles;
        let newAffectedFiles = affectedFiles.map((file) => {
            return {
                Id: file.uid,
                uid: file.uid,
                Name: file.name,
                name: file.name,
                MimeType: file.extension,
                extension: file.extension,
                FileString: '',
                progress: 100,
                size: '',
                FileStatus: UploadFileStatus.Removing,
                status: UploadFileStatus.Removing,
            };
        });

        setAffectedFiles(newAffectedFiles);

        setFiles(event.newState);
    }, [affectedFiles, onFilesChange, isDisabled]);

    const onProgress = useCallback(async (event) => {
        let newFiles = event.newState;
        newFiles.forEach((file) => {
            if (file.progress === 100) {
                file.status = UploadFileStatus.Uploaded;
            }
        });
        setFiles(newFiles);
    }, []);

    const onStatusChange = useCallback((event) => {
        setFiles(event.newState);
    }, []);

    const processFile = useCallback(async (currentFile, onProgress) => {
        return new Promise((resolve, reject) => {
            const uid = currentFile.uid;

            if (
                currentFile.validationErrors &&
                currentFile.validationErrors.length > 0
            ) {
                reject({
                    uid: uid,
                });
            } else {
                const reader = new FileReader();
                reader.onloadstart = (event) => {
                    //console.log("reader.onloadstart: ", event); // uncomment this for debugging purposes
                };
                reader.onload = (event) => {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    if (reader.result && typeof reader.result === "string") {
                        const base64Result = reader.result.split(",")[1];
                        const newBase64File = {
                            Id: currentFile.uid,
                            uid: currentFile.uid,
                            Name: currentFile.name,
                            name: currentFile.name,
                            MimeType: currentFile.extension,
                            extension: currentFile.extension,
                            FileString: base64Result,
                            progress: progress,
                            size: currentFile.size,
                            FileStatus: UploadFileStatus.Uploaded,
                            status: UploadFileStatus.Uploading,
                        };
                    } else {
                        reject({
                            uid: uid,
                        });
                    }
                };
                reader.onerror = (event) => {
                    reject({
                        uid: uid,
                    });
                };
                reader.onprogress = (data) => {
                    onProgress(uid, data);
                };
                reader.onabort = () => {
                    reject({
                        uid: uid,
                    });
                };
                reader.onloadend = async (event) => {
                    if (reader.result && typeof reader.result === "string") {
                        const base64Result = reader.result.split(",")[1];
                        const newBase64File = {
                            Id: currentFile.uid,
                            uid: currentFile.uid,
                            Name: currentFile.name,
                            name: currentFile.name,
                            MimeType: currentFile.extension,
                            extension: currentFile.extension,
                            FileString: base64Result,
                            progress: 100,
                            size: currentFile.size,
                            FileStatus: UploadFileStatus.Uploaded,
                            status: UploadFileStatus.Uploaded,
                        };
                        // Signal 100% progress to Kendo to ensure progress bar updates
                        onProgress(uid, { loaded: 1, total: 1 });
                        resolve(newBase64File);
                    } else {
                        reject({
                            uid: uid,
                        });
                    }
                };
                reader.readAsDataURL(currentFile.getRawFile());
            }
        });
    }, []);

    const onSaveRequest = useCallback(async (files, options, onProgress) => {
        // Create promises for each file - map returns an array of Promises
        const promises = files.map((currentFile) => processFile(currentFile, onProgress));

        try {
            // Wait for all files to be processed
            const newAffectedFiles = await Promise.all(promises);
            // Only set affected files if we got valid results
            if (newAffectedFiles && newAffectedFiles.length > 0) {
                setAffectedFiles(newAffectedFiles);
            }
        } catch (error) {
            console.error("Error processing files:", error);
        }
    }, [processFile]);

    const onRemoveRequest = useCallback(async (files) => {
        const currentFile = files[0];
        const uid = currentFile.uid;
        return new Promise((resolve) => {
            resolve({
                uid: uid,
            });
        });
    }, []);

    const onCancel = (event) => {
        console.log("onCancel: ", event);
    };

    const onDownloadAll = async (allFiles) => {
        allFiles.forEach(async (file) => {
            await downloadSingleFile(file);
        });
    };

    const onDownload = async (file) => {
        const FileId = file.Id || file.uid;
        const FileName = file.Name || file.name;
        const allFilesUpdated = [...base64Files, ...initialFiles];
        const updatedFile = allFilesUpdated.find(f => (f.Id === FileId || f.uid === FileId) && (f.Name === FileName || f.name === FileName));
        downloadSingleFile(updatedFile);
    };

    const downloadSingleFile = async (file) => {
        const { Id, uid, Name, name, MimeType, extension } = file;
        const FileId = Id || uid;
        const FileName = Name || name;
        const FileExtension = MimeType || extension;
        let FileString = file.FileString ?? "";

        if ((!FileString || FileString == "")) {
            const dbFile = await GetDynamicFormFieldFile(FileId);

            if (dbFile?.data && dbFile?.data?.FileString?.length > 0) {
                FileString = dbFile?.data?.FileString;
            } else {
                console.log("FileString is empty"); //right now this is the only control to don't try to download corrupted files
                return;
            }
        }

        saveFileString(FileName, FileString, FileExtension);
    };

    const onDownloadFiles = async (uid) => {
        const allFilesUpdated = [...base64Files, ...initialFiles];
        const files = allFilesUpdated.filter(file => (file.Id === uid || file.uid === uid));
        files.forEach(async (file) => {
            await downloadSingleFile(file);
        });
    };

    // Keep the ref updated with the latest onFilesChange callback
    useEffect(() => {
        onFilesChangeRef.current = onFilesChange;
    }, [onFilesChange]);

    // Keep the ref updated with the latest base64Files state
    useEffect(() => {
        base64FilesRef.current = base64Files;
    }, [base64Files]);

    useEffect(() => {
        //Default files are read only once. Any re-render in the application will mutate the array with more objects.
        setInitialFiles(defaultFiles);
        setAllFiles(defaultFiles);
        setFirstRenderCompleted(true);
    }, []);

    // Sync internal state when defaultFiles is cleared externally (e.g., form reset)
    useEffect(() => {
        if (firstRenderCompleted && (!defaultFiles || defaultFiles.length === 0)) {
            // Report initial files as "Removing" so the backend knows to delete them.
            // This matches the same format used by manual one-by-one file removal (onRemove),
            // ensuring the save payload includes files with FileStatus: Removing.
            if (initialFiles.length > 0 && onFilesChangeRef.current) {
                const removingFiles = initialFiles.map((file) => ({
                    Id: file.Id,
                    uid: file.Id,
                    Name: file.Name,
                    name: file.Name,
                    MimeType: file.MimeType,
                    extension: file.MimeType,
                    FileString: '',
                    progress: 100,
                    size: '',
                    FileStatus: UploadFileStatus.Removing,
                    status: UploadFileStatus.Removing,
                }));
                removedInitialFilesRef.current = removingFiles;
                onFilesChangeRef.current(removingFiles);
            }

            setInitialFiles([]);
            setAllFiles([]);
            setFiles([]);
            setBase64Files([]);
            setAffectedFiles([]);
            // Force Kendo Upload to remount so its internal state is fully cleared
            setUploadResetKey((prev) => prev + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultFiles]);

    useEffect(() => {
        if (firstRenderCompleted) {
            addDownloadButtons();
        }
    }, [firstRenderCompleted]);

    useEffect(() => {
        addDownloadButtons();
    }, [allFiles]);

    useEffect(() => {
        if (affectedFiles?.length > 0) {
            // Compute new state values using the ref (which has the latest value)
            let updatedBase64Files = [...base64FilesRef.current];

            affectedFiles.forEach((affectedFile) => {
                const index = updatedBase64Files.findIndex(
                    (file) => (file.Id === affectedFile.Id && file.Name === affectedFile.Name)
                );

                if (index !== -1) {
                    updatedBase64Files[index] = affectedFile;
                } else {
                    updatedBase64Files.push(affectedFile);
                }
            });

            // Compute derived values
            const updatedAllFiles = updatedBase64Files.filter((file) => {
                return file.FileStatus === UploadFileStatus.Uploaded;
            });

            const filesToReport = updatedBase64Files.filter((file) => {
                const isInitialFile = initialFiles.some((initialFile) => initialFile.Id === file.Id);
                return (!isInitialFile && file.FileStatus === UploadFileStatus.Uploaded) ||
                    (isInitialFile && file.FileStatus === UploadFileStatus.Removing);
            });

            // Update all states (pure state updates)
            setBase64Files(filesToReport);
            setAllFiles(updatedAllFiles);
            
            // Include any initial files that were removed during a reset,
            // so the backend receives both the new uploads and the delete instructions
            if (onFilesChangeRef.current) {
                const pendingRemovals = removedInitialFilesRef.current.filter(
                    (rf) => !filesToReport.some((f) => f.Id === rf.Id)
                );
                onFilesChangeRef.current([...pendingRemovals, ...filesToReport]);
            }
        }
    }, [affectedFiles, initialFiles]);

    const addDownloadButtons = () => {
        const fileItems = document.querySelectorAll('.k-file');
        fileItems.forEach(item => {
            const uid = item.getAttribute('data-uid');
            if (!item.querySelector('.custom-download-button')) {
                const container = document.createElement('span');
                container.className = 'k-upload-action custom-download-button';

                const buttonContainer = document.createElement('div');
                buttonContainer.innerHTML = `<span class="k-icon k-font-icon k-i-download k-icon-lg"></span>`;
                const buttonElement = buttonContainer.firstChild;
                buttonElement.onclick = () => onDownloadFiles(uid);

                container.appendChild(buttonElement);
                const actionButtons = item.querySelector('.k-upload-actions');
                if (actionButtons) {
                    actionButtons.appendChild(container);
                }
            }
        });
    };

    useEffect(() => {
        const observer = new MutationObserver(() => {
            addDownloadButtons();
        });

        const uploadFilesElement = document.querySelector('.k-upload-files');
        if (uploadFilesElement) {
            observer.observe(uploadFilesElement, { childList: true, subtree: true });
        }

        return () => observer.disconnect();
    }, []);

    const hint = (
        <span>
            <FluentUiIcon
                name={"DocumentArrowUpRegular"}
                color={"#424242"}
                size={20}
            />
            Drag files here to upload
        </span>
    );

    const footer =
        <span className="custom-footer">
            {`Max. file size: ${maxFileSizeForLibroMB}MB. Supported file types: documents (DOC/X, XLS/X, CSV, PPT/X, PDF), emails (EML, MSG), and images (PNG, JPG, GIF, SVG, TIFF).`}
        </span>;

    return (
        firstRenderCompleted &&
        <div className={isDisabled ? "upload-readonly" : undefined}>
            {!fromForm && <div style={{ height: "2rem" }} />}
            <Upload
                key={uploadResetKey}
                ref={uploadRef}
                batch={true}
                restrictions={{
                    allowedExtensions: validFileTypesForLibro,
                    maxFileSize: maxFileSizeForLibro,
                }}
                multiple={true}
                files={files}
                defaultFiles={Array.isArray(initialFiles) && initialFiles.length > 0 ? initialFiles.map((file) => ({
                    uid: file.Id,
                    name: file.Name,
                    extension: file.MimeType,
                    progress: 0,
                    status: UploadFileStatus.Initial,
                })) : []}
                onAdd={onAdd}
                onRemove={onRemove}
                onProgress={onProgress}
                onStatusChange={onStatusChange}
                onCancel={onCancel}
                withCredentials={false}
                saveUrl={onSaveRequest}
                removeUrl={onRemoveRequest}
                autoUpload={true}
                //listItemUI={CustomFileList} // Customized file list component
            />
            <div style={{ height: "3px" }} />
            <div style={{ height: "3px" }} />
            {showDropZone && !isDisabled &&
                <ExternalDropZone
                    className="custom-dropzone"
                    uploadRef={uploadRef}
                    customHint={externalDropZoneHint === "" ? hint : externalDropZoneHint}
                    customNote={externalDropZoneNote}
                    style={{ border: "1px dashed #888" }}
                />
            }
            {footer}
            <div style={{ height: "5px" }} />
            <Button
                type="button"
                icon="download"
                title="Download All"
                look="flat"
                style={{ float: "right", marginBottom: "8px" }}
                onClick={async (event) => {
                    const allFiles = [...base64Files];
                    initialFiles.forEach((initialFile) => {
                        const fileExists = allFiles.some((file) => file.Id === initialFile.Id);
                        if (!fileExists) {
                            allFiles.push(initialFile);
                        }
                    });

                    await onDownloadAll(allFiles);
                }}
            >
                Download All
            </Button>
        </div>
    );
});

CustomFileDropZone.propTypes = {
    defaultFiles: PropTypes.array,
    onFilesChange: PropTypes.func,
    showDropZone: PropTypes.bool,
    externalDropZoneHint: PropTypes.string,
    externalDropZoneNote: PropTypes.string,
    fromForm: PropTypes.bool,
    isDisabled: PropTypes.bool,
};

export default CustomFileDropZone;
